import { Grid, Typography, useTheme } from "@mui/material";
import { Form, Formik } from "formik";
import { MFTextField } from "../../../lib/formik";
import { useEffect, useState } from "react";
import { getProfileDetailsResponseBody, ProfileDetailsKeys } from "../types";
import { useDispatch, useSelector } from "react-redux";
import { getProfileDetailsUserId } from "../actions";
import { useSnackbar } from "notistack";
import { SkeletonTextField } from "../lib/UI";
import { preventSpecialCharacters } from "../../../utils/utilityFunctions";
import { CountryCodesDropDown } from "../../commonComponents";
import { RootStateType } from "../../../redux-store/reducers";
import { TextDatePicker } from "../../onboarding/DatePickerWithTextFeild";
import dayjs from "dayjs";

const initialValues = {
    name: '',
    pan: '',
    mobile: '',
    email: '',
    subBrokerARNCode: '',
    countryCode: '',
    countryNameAndCode: '',
    subBrokerARNExpiryDate: '',
    subBrokerARNStartDate: ''
}

export default function Details() {
    const theme = useTheme();
    const [isLoading, setLoading] = useState<ProfileDetailsKeys | null>();
    const [values, setValues] = useState<typeof initialValues>(initialValues);
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [mobileElementWidth, setMobileElementWidth] = useState(null);
    const { iwappRmId = '' } = useSelector((state: RootStateType) => state.id);

    async function getProfileDetails() {
        try {
            setLoading(ProfileDetailsKeys.GET_PROFILE_DETAILS);
            const payload = { id: `${iwappRmId}` };
            const res = await dispatch(getProfileDetailsUserId(payload)) as unknown as getProfileDetailsResponseBody;
            if (!res || !res.isSuccess) {
                throw new Error(res.message);
            }
            setValues((prev) => ({
                ...prev,
                name: res.data?.name,
                mobile: res.data?.mobile,
                countryCode: res.data?.countryCode,
                email: res.data?.email,
                subBrokerARNCode: res.data?.subBrokerARNCode,
                countryNameAndCode: res.data?.countryNameAndCode,
                subBrokerARNExpiryDate: res.data?.subBrokerARNExpiryDate
                    ? dayjs(res.data.subBrokerARNExpiryDate, "DD/MM/YYYY").toISOString()
                    : '',
                subBrokerARNStartDate: res.data?.subBrokerARNStartDate
                    ? dayjs(res.data.subBrokerARNStartDate, "DD/MM/YYYY").toISOString()
                    : ''
            }));
        } catch (e: any) {
            enqueueSnackbar(e.message || 'Self Declaration Failed', {
                variant: 'error',
                autoHideDuration: 3000,
            });
        } finally {
            setLoading(null);
        }
    }

    useEffect(() => {
        getProfileDetails();
        const mobileElement = document.getElementsByName('mobile');
        setMobileElementWidth(mobileElement[0]?.parentElement?.clientWidth as any);
    }, []);


    const handleSubmit = (values: typeof initialValues) => {
        console.log(values, 'submitted');
    };

    return (
        <>
            <Typography variant='h2' sx={{ color: theme.palette.primary[500] }}>
                Profile Details
            </Typography>
            <Formik
                initialValues={values}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ dirty }) => (
                    <Form>
                        <Grid container columnSpacing={2} rowSpacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                                {isLoading == ProfileDetailsKeys.GET_PROFILE_DETAILS
                                    ? <SkeletonTextField />
                                    : <MFTextField
                                        name={`name`}
                                        label="Name *"
                                        placeholder="Enter Name"
                                        disabled={true}
                                    />}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {isLoading == ProfileDetailsKeys.GET_PROFILE_DETAILS
                                    ? <SkeletonTextField />
                                    : <MFTextField
                                        name={`subBrokerARNCode`}
                                        label="ARN Code *"
                                        placeholder="Enter ARN Code"
                                        disabled={true}
                                    />}
                            </Grid>
                            {/* <Grid item xs={12} sm={6}>
                                <MFTextField
                                    name={`pan`}
                                    label="PAN *"
                                    placeholder="Enter PAN"
                                    disabled={true}
                                />
                            </Grid> */}
                            <Grid item xs={12} sm={6}>
                                {isLoading == ProfileDetailsKeys.GET_PROFILE_DETAILS
                                    ? <SkeletonTextField />
                                    : <MFTextField
                                        name={`mobile`}
                                        label="Phone Number *"
                                        placeholder="Enter Phone Number"
                                        disabled={true}
                                        onKeyDown={(e) => {
                                            preventSpecialCharacters(e);
                                        }}
                                        startAdornment={
                                            <CountryCodesDropDown
                                                ElementWidth={mobileElementWidth && mobileElementWidth}
                                                name={'countryNameAndCode'}
                                                disabled={true}
                                                value={values.countryNameAndCode}
                                            />
                                        }
                                    />}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {isLoading == ProfileDetailsKeys.GET_PROFILE_DETAILS
                                    ? <SkeletonTextField />
                                    : <MFTextField
                                        name={`email`}
                                        label="Email *"
                                        placeholder="Enter Email"
                                        disabled={true}
                                    />}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {isLoading == ProfileDetailsKeys.GET_PROFILE_DETAILS
                                    ? <SkeletonTextField />
                                    : <TextDatePicker
                                        label={'ARN Start Date( DD/MM/YYYY)*'}
                                        inputLabelStyles={{
                                            transform: 'unset',
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: 'rgba(0,0,0,0.7)',
                                        }}
                                        placeholder={'ARN Start Date( DD/MM/YYYY)'}
                                        name={`subBrokerARNStartDate`}
                                        disabled={true}
                                    />}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {isLoading == ProfileDetailsKeys.GET_PROFILE_DETAILS
                                    ? <SkeletonTextField />
                                    : <TextDatePicker
                                        label={'ARN Expiry Date( DD/MM/YYYY)*'}
                                        inputLabelStyles={{
                                            transform: 'unset',
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: 'rgba(0,0,0,0.7)',
                                        }}
                                        placeholder={'ARN Expiry Date( DD/MM/YYYY)'}
                                        name={`subBrokerARNExpiryDate`}
                                        disabled={true}
                                    />}
                            </Grid>
                            {/* <Grid item xs={12} sx={{ display: { xs: 'none', sm: 'block' } }} />
                            <Grid item xs={12} sm={3}>
                                <MFSubmitButton label="Submit" disabled={!dirty} />
                            </Grid> */}
                        </Grid>
                    </Form>
                )}
            </Formik>
        </>
    )
}
